import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { ItemSortOrder } from '@/contexts/item-context-types';
import { List, Props as ListProps } from '@/components/list';
import { ListOption } from '@/components/list-item';
import { RadioButton } from '@/components/radio-button';
import { SortOrders } from '@/contexts/user-context';
import { useItemDisplayOptions } from '@/contexts/new-order-context';
import { useLayoutContext } from '@/contexts/layout-context';

type SortOrderOptionType = ListOption<{
  isSelected: boolean;
}>;

interface Props<T> {
  ItemSeparatorComponent?: ListProps<T>['ItemSeparatorComponent'];
  onChange: (sortOrder: T) => void;
  supportedSortOptions: T[];
  type?: 'radio' | 'list';
  value: T;
}

export const sortOrderTranslationMap: { [key in SortOrders]: string } = {
  ALPHABETICAL: i18n.t('sort_order.a_z'),
  ALPHABETICAL_REVERSE: i18n.t('sort_order.z_a'),
  CUSTOM: i18n.t('sort_order.custom'),
  ITEM_CODE: i18n.t('sort_order.item_code'),
  ITEM_CODE_REVERSE: i18n.t('sort_order.item_code_reverse'),
  ORDER_FREQUENCY: i18n.t('sort_order.most_frequent'),
  ORDER_FREQUENCY_REVERSE: i18n.t('sort_order.least_frequent'),
  PURCHASED_DATE: i18n.t('sort_order.purchased_date'),
  RELEVANCE: i18n.t('sort_order.relevance'),
};

export const SortOrderSelectList = <T extends SortOrders = ItemSortOrder>({
  ItemSeparatorComponent,
  onChange,
  supportedSortOptions,
  type = 'list',
  value,
}: Props<T>) => {
  const { isSmallLayout } = useLayoutContext();
  const { displayItemCode } = useItemDisplayOptions();
  const [styles] = useStyles();

  const sortOrderData = useMemo(() => {
    return supportedSortOptions.reduce<SortOrderOptionType[]>(
      (acc, orderType) => {
        if (
          displayItemCode ||
          (orderType !== ItemSortOrder.ItemCode &&
            orderType !== ItemSortOrder.ItemCodeReverse)
        ) {
          acc.push({
            metadata: {
              isSelected: value === orderType,
            },
            title: sortOrderTranslationMap[orderType],
            value: orderType,
          });
        }

        return acc;
      },
      [],
    );
  }, [displayItemCode, supportedSortOptions, value]);

  return (
    <List<SortOrderOptionType>
      data={sortOrderData}
      ItemSeparatorComponent={ItemSeparatorComponent}
      listItemStyle={(option) => {
        const itemStyle = [];

        if (!isSmallLayout) {
          itemStyle.push(styles.largeLayoutListItem);
        }

        if (option.metadata?.isSelected && type === 'list') {
          itemStyle.push(styles.selectedBackground);
        }

        if (type === 'radio') {
          itemStyle.push(styles.radioItem);
        }

        return itemStyle;
      }}
      onSelect={(option) => {
        Analytics.track('order_guide.sort_order.select', {
          sortOrder: option.value,
        });
        onChange(option.value as T);
      }}
      renderRightComponent={(option) => {
        return type === 'radio' ? (
          <RadioButton selected={option.value === value} />
        ) : null;
      }}
    />
  );
};

const useStyles = createStyles((theme) => ({
  largeLayoutListItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  radioItem: {
    paddingHorizontal: 0,
  },
  selectedBackground: {
    backgroundColor: theme.colors.accentLight,
  },
}));
